import { useMemo } from "react";
import {
  buildCursorPath,
  parseSessionWithSetsRows,
  type FetchPage,
  type SessionWithSets,
} from "@heraion/shared";

import { useAsyncList } from "../useAsyncList";
import type { ProgressApiFetch } from "../Progress/types";

// #25: the athlete's sessions for the coach, newest first, with their sets,
// one page at a time over GET /api/sessions?athleteSub=&include=sets. The
// server serves only the sessions since the bond began. GET /api/sessions/:id
// stays self-only: web opens a session from these rows, mobile reads it again
// on the same endpoint (CoachAthleteSessionScreen).

// Each row fans out to a sets read server-side, so pages stay small.
const PAGE_SIZE = 20;

export type AthleteSessionsList = {
  /** null while the first page loads. */
  items: SessionWithSets[] | null;
  /** The failure's code ('api_error_<status>'); null = none. */
  error: string | null;
  cursor: string | null;
  refreshing: boolean;
  loadingMore: boolean;
  onRefresh: () => void;
  onEndReached: () => void;
};

export function useAthleteSessions(
  apiFetch: ProgressApiFetch,
  athleteSub: string,
): AthleteSessionsList {
  const fetchPage = useMemo<FetchPage<SessionWithSets, string>>(
    () => async (cursor) => {
      const res = await apiFetch<{ items?: unknown[]; cursor?: string | null }>(
        buildCursorPath(
          "/api/sessions",
          `athleteSub=${encodeURIComponent(athleteSub)}&include=sets&limit=${PAGE_SIZE}`,
          cursor,
        ),
      );
      return {
        items: parseSessionWithSetsRows(res.items ?? []),
        cursor: res.cursor ?? null,
      };
    },
    // apiFetch is a stable per-surface seam (see useAthleteDetail).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [athleteSub],
  );
  const list = useAsyncList({ key: `coach-sessions:${athleteSub}`, fetchPage });
  // useAsyncList keeps the previous rows until a new key's first page lands:
  // never show one athlete's sessions under another's name.
  const items = useMemo(
    () =>
      list.items?.filter((r) => r.session.userSub === athleteSub) ?? null,
    [list.items, athleteSub],
  );
  return {
    items,
    error:
      list.status === "error" && list.error
        ? `api_error_${list.error.status ?? 0}`
        : null,
    cursor: list.cursor,
    refreshing: list.status === "refreshing",
    loadingMore: list.status === "loading_more",
    onRefresh: () => void list.refresh(),
    onEndReached: () => void list.loadMore(),
  };
}
